import { useState, useCallback } from 'react';
import type { SupportedLanguage } from '../types';

const STORAGE_KEY = 'hwg_language_preference';

export type LanguageSource =
  | 'partner'
  | 'cf'
  | 'edge_llm'
  | 'user_cf'
  | 'user_implicit'
  | 'user_explicit';

export interface LanguageChange {
  language: string;
  previous_language: string;
  source: LanguageSource;
  timestamp: string;
}

export interface LanguageState {
  current: string;
  source: LanguageSource;
  partner_language: string;
  cf_language: string | null;
  show_cf_suggestion: boolean;
  has_stored_preference: boolean;  // True if user has made a language choice before
  history: LanguageChange[];
}

interface StoredPreference {
  language: string;
  source: LanguageSource;
  updated_at: string;
}

export interface UseLanguageConfig {
  partner_language: string;
  supported_languages?: SupportedLanguage[];
}

export interface UseLanguageReturn {
  state: LanguageState;
  setLanguage: (language: string, source: LanguageSource) => void;
  setCfLanguage: (language: string) => void;
  dismissCfSuggestion: () => void;
  isLanguageSupported: (language: string) => boolean;
}

function loadStoredPreference(): StoredPreference | null {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      return JSON.parse(stored);
    }
  } catch {
    // Ignore parse errors
  }
  return null;
}

function savePreference(language: string, source: LanguageSource): void {
  try {
    const preference: StoredPreference = {
      language,
      source,
      updated_at: new Date().toISOString(),
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(preference));
  } catch {
    // localStorage not available
  }
}

export function useLanguage(config: UseLanguageConfig): UseLanguageReturn {
  const { partner_language, supported_languages = [] } = config;

  // Initialize state
  const [state, setState] = useState<LanguageState>(() => {
    const stored = loadStoredPreference();

    // Check if stored language is still supported
    const supportedCodes = supported_languages.map(l => l.code);
    const isStoredSupported = stored &&
      (supportedCodes.length === 0 || supportedCodes.includes(stored.language));

    if (stored && isStoredSupported) {
      return {
        current: stored.language,
        source: stored.source,
        partner_language,
        cf_language: null,
        show_cf_suggestion: false,
        has_stored_preference: true,  // Returning user
        history: [],
      };
    }

    return {
      current: partner_language,
      source: 'partner',
      partner_language,
      cf_language: null,
      show_cf_suggestion: false,
      has_stored_preference: false,  // First-time visitor
      history: [],
    };
  });

  // Check if a language is supported
  const isLanguageSupported = useCallback((language: string): boolean => {
    if (supported_languages.length === 0) return true;
    return supported_languages.some(l => l.code === language);
  }, [supported_languages]);

  // Set language with source tracking and validation
  const setLanguage = useCallback((language: string, source: LanguageSource) => {
    // CRITICAL: Validate language is supported before accepting
    // This prevents EdgeLLM/fallback from setting unsupported languages
    if (!isLanguageSupported(language)) {
      console.warn(`[useLanguage] Rejected unsupported language: ${language} (source: ${source})`);
      return; // Reject the change - keep current language
    }

    setState(prev => {
      if (prev.current === language && prev.source === source) {
        return prev;
      }

      const change: LanguageChange = {
        language,
        previous_language: prev.current,
        source,
        timestamp: new Date().toISOString(),
      };

      // Persist user selections
      const isUserSelection = source === 'user_explicit' || source === 'user_cf' || source === 'user_implicit';
      if (isUserSelection) {
        savePreference(language, source);
      }

      return {
        ...prev,
        current: language,
        source,
        // User made a choice - never show CF suggestion again
        show_cf_suggestion: false,
        has_stored_preference: isUserSelection ? true : prev.has_stored_preference,
        history: [...prev.history, change],
      };
    });
  }, [isLanguageSupported]);

  // Set CF-detected language
  // Only show suggestion for first-time visitors (no stored preference)
  const setCfLanguage = useCallback((language: string) => {
    setState(prev => ({
      ...prev,
      cf_language: language,
      // Only suggest if: first-time visitor AND language differs AND language is supported
      show_cf_suggestion: !prev.has_stored_preference &&
        language !== prev.current &&
        isLanguageSupported(language),
    }));
  }, [isLanguageSupported]);

  // Dismiss CF suggestion
  const dismissCfSuggestion = useCallback(() => {
    setState(prev => ({
      ...prev,
      show_cf_suggestion: false,
    }));
  }, []);

  return {
    state,
    setLanguage,
    setCfLanguage,
    dismissCfSuggestion,
    isLanguageSupported,
  };
}
